import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import { residentsAPI } from '../../services/api';
import {
  getResidentPhotoUrl,
  getResidentInitials,
} from '../../utils/photoUtils';
import {
  PlusIcon,
  UserIcon,
  TrashIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// Import utility functions
import {
  addFamilyMember,
  editFamilyMember,
  viewFamilyMember,
  confirmDeleteFamilyMember,
  deleteFamilyMember,
  cancelDeleteFamilyMember,
  validateFamilyMember,
  resetFamilyMemberForm,
  handleFamilyMembersCreation,
  handleFamilyMembersUpdate,
} from '../../utils/residentFunctions';

// Import containerized components
import DemographicsDashboard from '../../components/residents/cards/DemographicsDashboard';
import ResidentsTable from '../../components/residents/ResidentsTable';
import ViewResidentModal from '../../components/residents/modals/ViewResidentModal';
import PrintModal from '../../components/residents/modals/PrintModal';

// Import family member modals
import FamilyMemberFormModal from '../../components/residents/modals/FamilyMemberFormModal';
import FamilyMemberViewModal from '../../components/residents/modals/FamilyMemberViewModal';
import DeleteFamilyMemberModal from '../../components/residents/modals/DeleteFamilyMemberModal';
import PhotoCaptureModal from '../../components/residents/modals/PhotoCaptureModal';
import CreateFamilyModal from '../../components/residents/modals/CreateFamilyModal';
import FamilyModal from '../../components/residents/modals/FamilyModal';
import AssignHouseholdModal from '../../components/residents/modals/AssignHouseholdModal';
import { AuditTrailModal } from '../../components/residents/modals/AuditTrail';
import { useQuery } from 'react-query';
import ResidentFormModal from '../../components/residents/modals/RsidentFormModal';
import { stats } from '../../components/residents/const/Consts';
import StatisticsCard from '../../components/common/StatisticsCard';

const ResidentsPage = () => {
  // State management
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isResidentFetching, setIsResidentFetching] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showDemographicsDetail, setShowDemographicsDetail] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [residentToDelete, setResidentToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);

  // Enhanced form state variables
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showFamilyMemberForm, setShowFamilyMemberForm] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);

  const [showFamilyMemberViewModal, setShowFamilyMemberViewModal] =
    useState(false);
  const [showDeleteFamilyMemberModal, setShowDeleteFamilyMemberModal] =
    useState(false);
  const [showPhotoCaptureModal, setShowPhotoCaptureModal] = useState(false);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState(null);
  const [familyMemberToDelete, setFamilyMemberToDelete] = useState(null);
  const [editingFamilyMember, setEditingFamilyMember] = useState(null);
  const [isDeletingFamilyMember, setIsDeletingFamilyMember] = useState(false);
  const [newFamilyMember, setNewFamilyMemberState] = useState({
    name: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    relationship: '',
    relationship_to_head: '',
    age: '',
    birthdate: '',
    birthplace: '',
    gender: '',
    occupation: '',
  });

  // Debug wrapper for setNewFamilyMember
  const setNewFamilyMember = (data) => {
    setNewFamilyMemberState(data);
  };

  // Debug effect to track newFamilyMember state changes
  useEffect(() => {
    // console.log('newFamilyMember state changed:', newFamilyMember);
  }, [newFamilyMember]);

  const [filters, setFilters] = useState({
    status: '',
    gender: '',
    civil_status: '',
    search: '',
    income: '',
    category: '',
    household: '',
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm();

  // COVERAGE DATA
  const selectedRegion = useWatch({ control, name: 'region' });
  const selectedProvince = useWatch({ control, name: 'province' });
  const selectedMunicipality = useWatch({ control, name: 'municipality' });
  const selectedBarangay = useWatch({ control, name: 'barangay' });

  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [barangays, setBarangays] = useState([]);

  const transformResident = useCallback((resident) => {
    if (!resident) return resident;

    return {
      ...resident,
      date_of_birth: resident.birthdate,
      phone_number: resident.contact_number,
      house_number:
        resident.address?.house_number || resident.house_number || '',
      street: resident.address?.street || resident.street || '',
      purok_zone: resident.address?.purok_zone || resident.purok_zone || '',
      barangay: resident.address?.barangay || resident.barangay || '',
      barangay_code:
        resident.address?.barangay_code || resident.barangay_code || '',
      municipality:
        resident.address?.municipality || resident.municipality || '',
      municipality_code:
        resident.address?.municipality_code || resident.municipality_code || '',
      province: resident.address?.province || resident.province || '',
      province_code:
        resident.address?.province_code || resident.province_code || '',
      region: resident.address?.region || resident.region || '',
      region_code: resident.address?.region_code || resident.region_code || '',
      zip_code: resident.address?.zip_code || resident.zip_code || '',
      address:
        typeof resident.full_address === 'string'
          ? resident.full_address
          : [
              resident.address?.house_number,
              resident.address?.street,
              resident.address?.purok_zone,
              resident.address?.barangay,
              resident.address?.barangay_code,
              resident.address?.municipality,
              resident.address?.municipality_code,
              resident.address?.province,
              resident.address?.province_code,
              resident.address?.region,
              resident.address?.region_code,
            ]
              .filter(Boolean)
              .join(', ') || 'No address provided',
      photo_url: resident.photo_url || resident.profile_photo,
      profile_photo: resident.profile_photo,
      qr_code: resident.barangay_id_number,
      precinct_number: resident.voter?.precinct_name,
      relationship_to_head: (() => {
        if (resident.is_household_head) return 'Head';
        if (
          resident.relationship_to_head &&
          resident.relationship_to_head !== 'Member'
        ) {
          return resident.relationship_to_head;
        }
        return 'Member';
      })(),
      emergency_contact: `${resident.emergency_contact_name} - ${resident.emergency_contact_number}`,
      household_members: Array.isArray(resident.household_members)
        ? resident.household_members
        : [],
    };
  }, []);

  // Helper function to get photo URL
  const getPhotoUrl = (resident) => {
    const url = getResidentPhotoUrl(resident);
    return url;
  };

  // Photo capture functions
  const openPhotoCapture = () => {
    setShowPhotoCaptureModal(true);
  };

  const handlePhotoCaptured = (file, dataUrl) => {
    setPhotoFile(file);
    setCapturedPhoto(dataUrl);
    setShowPhotoCaptureModal(false);
  };

  // Family member management functions
  const handleEditFamilyMember = (member) => {
    // Close the view modal first
    setShowFamilyMemberViewModal(false);
    // Then open the edit modal
    editFamilyMember(
      member,
      setEditingFamilyMember,
      setNewFamilyMember,
      setShowFamilyMemberForm
    );
  };

  const handleViewFamilyMember = (member) => {
    viewFamilyMember(
      member,
      setSelectedFamilyMember,
      setShowFamilyMemberViewModal
    );
  };

  const handleConfirmDeleteFamilyMember = (member) => {
    // Prevent multiple clicks while deletion is in progress
    if (isDeletingFamilyMember) {
      console.log('Delete operation in progress, ignoring click');
      return;
    }
    confirmDeleteFamilyMember(
      member,
      setFamilyMemberToDelete,
      setShowDeleteFamilyMemberModal
    );
  };

  const handleDeleteFamilyMember = async () => {
    // Prevent multiple concurrent calls
    if (isDeletingFamilyMember) {
      console.log('Delete already in progress, ignoring duplicate call');
      return;
    }

    try {
      setIsDeletingFamilyMember(true);
      await deleteFamilyMember(
        familyMemberToDelete,
        familyMembers,
        setFamilyMembers,
        setShowDeleteFamilyMemberModal,
        setFamilyMemberToDelete
      );
    } catch (error) {
      console.error('Error in handleDeleteFamilyMember:', error);
    } finally {
      setIsDeletingFamilyMember(false);
    }
  };

  const handleCancelDeleteFamilyMember = () => {
    // Reset loading state when cancelling
    setIsDeletingFamilyMember(false);
    cancelDeleteFamilyMember(
      setShowDeleteFamilyMemberModal,
      setFamilyMemberToDelete
    );
  };

  const handleAddFamilyMember = () => {
    resetFamilyMemberForm(
      setNewFamilyMember,
      setEditingFamilyMember,
      setShowFamilyMemberForm
    );
    setShowFamilyMemberForm(true);
  };

  const handleSaveFamilyMember = () => {
    // Construct full name from separate fields if not already set
    const fullName =
      newFamilyMember.name ||
      `${newFamilyMember.first_name || ''} ${
        newFamilyMember.middle_name || ''
      } ${newFamilyMember.last_name || ''}`.trim();

    const memberWithFullName = {
      ...newFamilyMember,
      name: fullName,
      // Ensure we have all name components
      first_name:
        newFamilyMember.first_name || newFamilyMember.name?.split(' ')[0] || '',
      last_name:
        newFamilyMember.last_name ||
        newFamilyMember.name?.split(' ').pop() ||
        '',
      middle_name: newFamilyMember.middle_name || '',
    };

    const validation = validateFamilyMember(memberWithFullName);

    if (!validation.isValid) {
      // Show specific validation errors
      const errorMessages = Object.values(validation.errors).join(', ');
      toast.error(`Please fix the following errors: ${errorMessages}`);
      return;
    }

    if (editingFamilyMember) {
      // Update existing member with proper field mapping
      const updatedMemberData = {
        ...editingFamilyMember,
        ...memberWithFullName,
        // Ensure relationship fields are consistent
        relationship:
          memberWithFullName.relationship_to_head ||
          memberWithFullName.relationship,
        relationship_to_head:
          memberWithFullName.relationship_to_head ||
          memberWithFullName.relationship,
      };

      const updatedMembers = familyMembers.map((member) =>
        member.id === editingFamilyMember.id ? updatedMemberData : member
      );
      setFamilyMembers(updatedMembers);
    } else {
      // Add new member with proper field mapping
      const newMemberData = {
        ...memberWithFullName,
        relationship:
          memberWithFullName.relationship_to_head ||
          memberWithFullName.relationship,
        relationship_to_head:
          memberWithFullName.relationship_to_head ||
          memberWithFullName.relationship,
        // If this is an existing resident, include additional metadata
        ...(memberWithFullName.existing_resident_id && {
          existing_resident_id: memberWithFullName.existing_resident_id,
          barangay_id_number: memberWithFullName.barangay_id_number,
          contact_number: memberWithFullName.contact_number,
          is_existing_resident: true,
        }),
      };
      addFamilyMember(familyMembers, setFamilyMembers, newMemberData);

      // Show success message if existing resident was added
      if (memberWithFullName.existing_resident_id) {
        toast.success(
          `${memberWithFullName.name} has been added as a family member`
        );
      }
    }

    // Close form
    resetFamilyMemberForm(
      setNewFamilyMember,
      setEditingFamilyMember,
      setShowFamilyMemberForm
    );
  };

  // Pagination change handler
  const handlePageChange = useCallback(
    (page, perPage) => {
      setPagination((prev) => ({ ...prev, currentPage: page, perPage }));
      fetchResidentsWithFilters(filters, true, page, perPage);
    },
    [filters]
  );

  const handleCancelFamilyMemberForm = () => {
    resetFamilyMemberForm(
      setNewFamilyMember,
      setEditingFamilyMember,
      setShowFamilyMemberForm
    );
  };

  // Fetch residents data with optimized loading states and pagination
  const fetchResidentsWithFilters = useCallback(
    async (
      filtersToUse = filters,
      showTableLoadingOnly = false,
      page = pagination.currentPage,
      perPage = pagination.perPage
    ) => {
      try {
        console.log(
          'Fetching residents with filters:',
          filtersToUse,
          'Page:',
          page,
          'Per page:',
          perPage
        );

        // Optimize loading states based on context
        if (showTableLoadingOnly) {
          setIsTableLoading(true);
        } else {
          setLoading(true);
          setIsTableLoading(false);
        }

        // Include pagination parameters in the request
        const params = {
          ...filtersToUse,
          page,
          per_page: perPage,
        };

        const response = await residentsAPI.getAll(params);

        // Transform data to match frontend expectations
        const transformedData = response.data.data.map((resident) => {
          const transformed = {
            // Preserve all original fields from backend
            ...resident,
            // Add computed/derived fields for frontend compatibility
            date_of_birth: resident.birthdate,
            phone_number: resident.contact_number,
            // Extract individual address fields from backend address object
            house_number: resident.address?.house_number || '',
            street: resident.address?.street || '',
            purok_zone: resident.address?.purok_zone || '',
            barangay: resident.address?.barangay || '',
            barangay_code: resident.address?.barangay_code || '',
            municipality: resident.address?.municipality || '',
            municipality_code: resident.address?.municipality_code || '',
            province: resident.address?.province || '',
            province_code: resident.address?.province_code || '',
            region: resident.address?.region || '',
            region_code: resident.address?.region_code || '',
            zip_code: resident.address?.zip_code || '',
            // Construct address string from components or use full_address if it's a string
            address:
              typeof resident.full_address === 'string'
                ? resident.full_address
                : [
                    resident.address?.house_number,
                    resident.address?.street,
                    resident.address?.purok_zone,
                    resident.address?.barangay,
                    resident.address?.municipality,
                    resident.address?.province,
                    resident.address?.region,
                  ]
                    .filter(Boolean)
                    .join(', ') || 'No address provided',
            // Handle photo fields - preserve both photo_url and profile_photo
            photo_url: resident.photo_url || resident.profile_photo,
            profile_photo: resident.profile_photo,
            qr_code: resident.barangay_id_number,
            household_id:
              resident.household_id ||
              `HH-${String(resident.id).padStart(4, '0')}`,
            relationship_to_head: (() => {
              if (resident.is_household_head) return 'Head';
              if (
                resident.relationship_to_head &&
                resident.relationship_to_head !== 'Member'
              ) {
                return resident.relationship_to_head;
              }
              return 'Member'; // Default fallback for family members
            })(),
            emergency_contact: `${resident.emergency_contact_name} - ${resident.emergency_contact_number}`,
            family_members: resident.household_members
              ? resident.household_members
                  .filter((member) => {
                    // Smart filtering: if current resident is head, show only family members
                    // if current resident is member, show head and other family members
                    if (resident.is_household_head) {
                      return (
                        member.id !== resident.id && !member.is_household_head
                      );
                    } else {
                      return member.id !== resident.id;
                    }
                  })
                  .map((member) => ({
                    id: member.id || Math.random(),
                    name: `${member.first_name} ${member.last_name}`,
                    relationship: member.is_household_head
                      ? 'Household Head'
                      : member.relationship_to_head ||
                        member.occupation ||
                        'Family Member',
                    age:
                      member.age ||
                      (member.birthdate
                        ? new Date().getFullYear() -
                          new Date(member.birthdate).getFullYear()
                        : 'N/A'),
                    birthdate: member.birthdate,
                    birthplace: member.birthplace,
                    first_name: member.first_name,
                    last_name: member.last_name,
                    gender: member.gender,
                    occupation: member.occupation || 'N/A',
                    relationship_to_head:
                      member.relationship_to_head || 'Family Member',
                    status: member.status || 'active',
                  }))
              : [],
            household_members: resident.household_members
              ? resident.household_members.filter(
                  (member) => member.id !== resident.id
                )
              : [],
            created_at: resident.created_at,
            updated_at: resident.updated_at,
          };

          return transformed;
        });

        setResidents(transformedData);

        // Update pagination data from response
        if (response.data.pagination) {
          setPagination({
            currentPage: response.data.pagination.current_page,
            lastPage: response.data.pagination.last_page,
            perPage: response.data.pagination.per_page,
            total: response.data.pagination.total,
          });
        }
      } catch (error) {
        console.error('Error fetching residents:', error);
        // Fallback to mock data if API fails
        const mockResidents = [
          {
            id: 1,
            first_name: 'Juan',
            last_name: 'Dela Cruz',
            date_of_birth: '1990-05-15',
            gender: 'male',
            civil_status: 'married',
            phone_number: '+63 912 345 6789',
            email: 'juan.delacruz@email.com',
            address: '123 Main Street, Barangay Center',
            status: 'active',
            barangay_id_number: 'BRG-2024-001',
            occupation: 'Teacher',
            emergency_contact: 'Maria Dela Cruz - +63 917 888 9999',
            photo_url: null,
            qr_code: 'BRG2024001JDC',
            household_id: 'HH-001',
            relationship_to_head: 'Head',
            family_members: [
              { name: 'Maria Dela Cruz', relationship: 'Spouse', age: 28 },
              { name: 'Jose Dela Cruz', relationship: 'Son', age: 5 },
              { name: 'Ana Dela Cruz', relationship: 'Daughter', age: 3 },
            ],
          },
        ];
        setResidents(mockResidents);

        // Set default pagination for mock data
        setPagination({
          currentPage: 1,
          lastPage: 1,
          perPage: 10,
          total: mockResidents.length,
        });
      } finally {
        setLoading(false);
        setIsTableLoading(false);
      }
    },
    []
  );
  const handleFiltersChange = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      fetchResidentsWithFilters(newFilters, true, 1, pagination.perPage);
    },
    [pagination.perPage, fetchResidentsWithFilters]
  );

  const handleSearchChange = useCallback((value) => {
    setFilters((prev) => ({
      ...prev,
      search: value,
    }));
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (filters.search !== undefined) {
        fetchResidentsWithFilters(filters, true, 1, pagination.perPage);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [filters.search]); // Only trigger on search change

  const refreshResidents = useCallback(() => {
    fetchResidentsWithFilters(
      filters,
      true,
      pagination.currentPage,
      pagination.perPage
    );
  }, [
    fetchResidentsWithFilters,
    filters,
    pagination.currentPage,
    pagination.perPage,
  ]);

  const { data: statistics } = useQuery({
    queryKey: ['resident-statistics'],
    queryFn: async () => {
      const { data } = await residentsAPI.getStatistics();
      return data;
    },
  });

  const fetchRegions = async () => {
    try {
      const res = await fetch('https://psgc.gitlab.io/api/regions/');
      setRegions(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const fetchProvinces = async (code) => {
    try {
      const res = await fetch(
        `https://psgc.gitlab.io/api/regions/${code}/provinces/`
      );
      setProvinces(await res.json());
    } catch {
      setProvinces([]);
    }
  };

  const fetchMunicipalities = async (code) => {
    try {
      const res = await fetch(
        `https://psgc.gitlab.io/api/provinces/${code}/cities-municipalities/`
      );
      setMunicipalities(await res.json());
    } catch {
      setMunicipalities([]);
    }
  };

  const fetchBarangays = async (code) => {
    try {
      const res = await fetch(
        `https://psgc.gitlab.io/api/cities-municipalities/${code}/barangays/`
      );
      setBarangays(await res.json());
    } catch {
      setBarangays([]);
    }
  };

  // Event handlers for resident actions
  const populateEditForm = useCallback(
    (resident) => {
      setSelectedResident(resident);
      setShowForm(true);
      setIsSubmitting(false);

      // Set existing photo if available - improved photo handling
      const photoUrl = getPhotoUrl(resident);

      if (photoUrl) {
        setCapturedPhoto(photoUrl);
        // Clear any new photo file since we're using existing photo
        setPhotoFile(null);
      } else {
        setCapturedPhoto(null);
        setPhotoFile(null);
      }

      // Always use household_members for consistent filtering, as family_members might be incorrectly pre-filtered
      if (
        resident.household_members &&
        Array.isArray(resident.household_members)
      ) {
        // Always filter to exclude the current resident, regardless of their role
        const filteredMembers = resident.household_members.filter((member) => {
          const isNotCurrentResident = member.id !== resident.id;
          return isNotCurrentResident;
        });

        const transformedMembers = filteredMembers.map((member) => ({
          id: member.id || Math.random(),
          name: `${member.first_name} ${member.last_name}`,
          relationship: member.is_household_head
            ? 'Household Head'
            : member.relationship_to_head ||
              member.occupation ||
              'Family Member',
          age:
            member.age ||
            (member.birthdate
              ? new Date().getFullYear() -
                new Date(member.birthdate).getFullYear()
              : 'N/A'),
          birthdate: member.birthdate,
          birthplace: member.birthplace,
          first_name: member.first_name,
          last_name: member.last_name,
          gender: member.gender,
          occupation: member.occupation || 'N/A',
          relationship_to_head: member.relationship_to_head || 'Family Member',
          profile_photo: member.profile_photo || null,
          status: member.status || 'active',
        }));

        console.log(
          'Transformed family members for editing:',
          transformedMembers
        );
        setFamilyMembers(transformedMembers);
      } else {
        console.log('No household_members found');
        setFamilyMembers([]);
      }

      // Map frontend data to backend format for editing
      const formData = {
        first_name: resident.first_name,
        middle_name: resident.middle_name,
        last_name: resident.last_name,
        suffix: resident.suffix,
        date_of_birth: resident.birthdate || resident.date_of_birth,
        birthplace: resident.birthplace,
        gender: resident.gender,
        civil_status: resident.civil_status,
        citizenship: resident.citizenship,
        religion: resident.religion,
        occupation: resident.occupation,
        monthly_income: resident.monthly_income,
        education_level: resident.education_level,
        phone_number: resident.contact_number || resident.phone_number,
        email: resident.email,
        emergency_contact_name: resident.emergency_contact_name,
        emergency_contact_number: resident.emergency_contact_number,
        house_number: resident.house_number,
        street: resident.street,
        purok_zone: resident.purok_zone,
        barangay: resident.barangay_code,
        // barangay_code: resident.barangay_code,
        municipality: resident.municipality_code,
        // municipality_code: resident.municipality_code,
        province: resident.province_code,
        // province_code: resident.province_code,
        region: resident.region_code,
        // region_code: resident.region_code,
        zip_code: resident.zip_code,
        length_of_residency: resident.length_of_residency,
        voter_status: resident.voter_status,
        precinct_number: resident.precinct_number,
        solo_parent_status: resident.solo_parent_status,
        pwd_status: resident.pwd_status,
        senior_citizen_status: resident.senior_citizen_status,
        illiterate_status: resident.illiterate_status,
        four_ps_number: resident.four_ps_number,
        indigenous_group: resident.indigenous_group,
        status: resident.status,
        // Ensure relationship_to_head is properly set for editing
        relationship_to_head: (() => {
          if (resident.is_household_head) return 'Head';
          if (
            resident.relationship_to_head &&
            resident.relationship_to_head !== 'Member'
          ) {
            return resident.relationship_to_head;
          }
          return 'Member'; // Default fallback for family members
        })(),
        household_id: resident.household_id,
      };

      // Use setTimeout to ensure form reset happens after all state updates
      setTimeout(() => {
        reset(formData);
        // Explicitly set the relationship_to_head value to ensure it's selected
        const relationshipValue = (() => {
          if (resident.is_household_head) return 'Head';
          if (
            resident.relationship_to_head &&
            resident.relationship_to_head !== 'Member'
          ) {
            return resident.relationship_to_head;
          }
          return 'Member'; // Default fallback for family members
        })();

        setValue('relationship_to_head', relationshipValue);
      }, 0);
    },
    [getPhotoUrl, reset, setValue]
  );

  const handleViewResident = useCallback(
    async (resident) => {
      try {
        setIsResidentFetching(true);
        const response = await residentsAPI.getById(resident.id);
        const fetchedResident = transformResident(response.data?.data);
        setSelectedResident(fetchedResident);
        setShowViewModal(true);
      } catch (error) {
        console.error('Error fetching resident details:', error);
        toast.error('Failed to load resident details. Please try again.');
      } finally {
        setIsResidentFetching(false);
      }
    },
    [transformResident]
  );

  const handleEditResident = useCallback(
    async (resident) => {
      try {
        setIsResidentFetching(true);
        const response = await residentsAPI.getById(resident.id);
        const fetchedResident = transformResident(response.data?.data);
        populateEditForm(fetchedResident);
      } catch (error) {
        console.error('Error fetching resident details:', error);
        toast.error('Failed to load resident details. Please try again.');
      } finally {
        setIsResidentFetching(false);
      }
    },
    [populateEditForm, transformResident]
  );

  const handlePrintResident = (resident) => {
    setSelectedResident(resident);
    setShowPrintModal(true);
  };

  const confirmDelete = (resident) => {
    setResidentToDelete(resident);
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setResidentToDelete(null);
  };

  const handleDeleteResident = async (residentId) => {
    try {
      await residentsAPI.delete(residentId);
      // Refresh the residents list with current filters
      refreshResidents();
      setShowDeleteModal(false);
      setResidentToDelete(null);
      toast.success('Resident deleted successfully');
    } catch (error) {
      console.error('Error deleting resident:', error);
      toast.error('Failed to delete resident. Please try again.');
    }
  };

  const resetResidentForm = () => {
    setShowForm(false);
    setSelectedResident(null);
    setIsSubmitting(false);
    setCapturedPhoto(null);
    setPhotoFile(null);
    setFamilyMembers([]);
    setShowFamilyMemberForm(false);
    reset();
  };
  const buildResidentPayload = (data) => {
    const chosenRegion = regions.find((r) => r.code === data.region);
    const chosenProvince = provinces.find((p) => p.code === data.province);
    const chosenMunicipality = municipalities.find(
      (m) => m.code === data.municipality
    );
    const chosenBarangay = barangays.find((b) => b.code === data.barangay);

    const isHead =
      data.is_household_head || data.relationship_to_head === 'Head';

    return {
      // Personal Information
      first_name: data.first_name,
      middle_name: data.middle_name || null,
      last_name: data.last_name,
      suffix: data.suffix || null,
      birthdate: data.date_of_birth,
      birthplace: data.birthplace,
      gender: data.gender,
      civil_status: data.civil_status || 'single',
      citizenship: data.citizenship,
      religion: data.religion || null,
      occupation: data.occupation || null,
      monthly_income: data.monthly_income
        ? parseFloat(data.monthly_income)
        : null,
      education_level: data.education_level || null,

      // Contact
      contact_number: data.phone_number || null,
      email: data.email || null,
      emergency_contact_name: data.emergency_contact_name || null,
      emergency_contact_number: data.emergency_contact_number || null,

      // Address
      house_number: data.house_number || null,
      street: data.street || null,
      purok_zone: data.purok_zone,
      barangay: chosenBarangay?.name || null,
      barangay_code: data.barangay,
      municipality: chosenMunicipality?.name || null,
      municipality_code: data.municipality,
      province: chosenProvince?.name || null,
      province_code: data.province,
      region: chosenRegion?.name || null,
      region_code: data.region,
      zip_code: data.zip_code || null,

      // Additional
      length_of_residency: data.length_of_residency || null,
      voter_status: data.voter_status || null,
      precinct_number: data.precinct_number || null,
      solo_parent_status: !!data.solo_parent_status,
      pwd_status: !!data.pwd_status,
      senior_citizen_status: !!data.senior_citizen_status,
      illiterate_status: !!data.illiterate_status,
      four_ps_number: data.four_ps_number || null,
      indigenous_group: data.indigenous_group || null,

      // System
      status: data.status || 'active',
      is_household_head: isHead,
      relationship_to_head: isHead ? 'Head' : data.relationship_to_head || null,
      household_id: data.household_id || null,
    };
  };

  const uploadResidentPhoto = async (residentId) => {
    if (!photoFile) return;
    const photoFormData = new FormData();
    photoFormData.append('photo', photoFile);
    await residentsAPI.uploadPhoto(residentId, photoFormData);
  };

  const generateBarangayId = () =>
    `${new Date().getFullYear()}-${String(
      Math.floor(Math.random() * 9999) + 1
    ).padStart(4, '0')}`;

  const isBecomingHead = (data) => {
    if (!selectedResident) return false;

    const newIsHead =
      data.is_household_head || data.relationship_to_head === 'Head';

    const oldIsHead =
      selectedResident.is_household_head ||
      selectedResident.relationship_to_head === 'Head';

    return newIsHead && !oldIsHead;
  };

  const handleSubmitResident = async (data) => {
    try {
      setIsSubmitting(true);

      const formData = buildResidentPayload(data);

      // Handle head reassignment
      if (isBecomingHead(data)) {
        await residentsAPI.assignToHousehold(
          selectedResident.id,
          selectedResident.id
        );
      }

      if (selectedResident) {
        // UPDATE FLOW
        await uploadResidentPhoto(selectedResident.id);
        await residentsAPI.update(selectedResident.id, formData);

        if (familyMembers.length > 0) {
          await handleFamilyMembersUpdate(
            selectedResident.id,
            familyMembers,
            formData
          );
        }
      } else {
        // CREATE FLOW
        formData.barangay_id_number = generateBarangayId();

        const response = await residentsAPI.create(formData);
        const newResidentId = response.data?.data?.id;

        if (!newResidentId) return;

        await uploadResidentPhoto(newResidentId);

        if (familyMembers.length > 0) {
          await handleFamilyMembersCreation(
            newResidentId,
            familyMembers,
            formData
          );
        }
      }

      refreshResidents();
      resetResidentForm();

      toast.success(
        selectedResident
          ? 'Resident updated successfully'
          : 'Resident registered successfully'
      );
    } catch (error) {
      console.error('Error submitting resident:', error);

      if (error.response?.status === 422) {
        const errors = Object.values(error.response.data.errors || {}).flat();
        toast.error(`Validation Error: ${errors.join(', ')}`);
      } else {
        toast.error(
          error.response?.data?.message ||
            'Failed to submit resident. Please try again.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Effects - Optimized to prevent unnecessary full page reloads
  useEffect(() => {
    // Only fetch residents on initial load with pagination
    const initialLoad = async () => {
      try {
        setLoading(true);
        const params = {
          ...filters,
          page: 1,
          per_page: 10,
        };
        const response = await residentsAPI.getAll(params);

        // Transform and set data (reusing the same logic)
        const transformedData = response.data.data.map((resident) => {
          const transformed = {
            ...resident,
            date_of_birth: resident.birthdate,
            phone_number: resident.contact_number,
            house_number: resident.address?.house_number || '',
            street: resident.address?.street || '',
            purok_zone: resident.address?.purok_zone || '',
            barangay: resident.address?.barangay || '',
            barangay_code: resident.address?.barangay_code || '',
            municipality: resident.address?.municipality || '',
            municipality_code: resident.address?.municipality_code || '',
            province: resident.address?.province || '',
            province_code: resident.address?.province_code || '',
            region: resident.address?.region || '',
            region_code: resident.address?.region_code || '',
            zip_code: resident.address?.zip_code || '',
            address:
              typeof resident.full_address === 'string'
                ? resident.full_address
                : [
                    resident.address?.house_number,
                    resident.address?.street,
                    resident.address?.purok_zone,
                    resident.address?.barangay,
                    resident.address?.municipality,
                    resident.address?.province,
                    resident.address?.region,
                  ]
                    .filter(Boolean)
                    .join(', ') || 'No address provided',
            photo_url: resident.photo_url || resident.profile_photo,
            profile_photo: resident.profile_photo,
            qr_code: resident.barangay_id_number,
            household_id:
              resident.household_id ||
              `HH-${String(resident.id).padStart(4, '0')}`,
            relationship_to_head: (() => {
              if (resident.is_household_head) return 'Head';
              if (
                resident.relationship_to_head &&
                resident.relationship_to_head !== 'Member'
              ) {
                return resident.relationship_to_head;
              }
              return 'Member';
            })(),
            emergency_contact: `${resident.emergency_contact_name} - ${resident.emergency_contact_number}`,
            family_members: resident.household_members
              ? resident.household_members
                  .filter((member) => {
                    if (resident.is_household_head) {
                      return (
                        member.id !== resident.id && !member.is_household_head
                      );
                    } else {
                      return member.id !== resident.id;
                    }
                  })
                  .map((member) => ({
                    id: member.id || Math.random(),
                    name: `${member.first_name} ${member.last_name}`,
                    relationship: member.is_household_head
                      ? 'Household Head'
                      : member.relationship_to_head ||
                        member.occupation ||
                        'Family Member',
                    age:
                      member.age ||
                      (member.birthdate
                        ? new Date().getFullYear() -
                          new Date(member.birthdate).getFullYear()
                        : 'N/A'),
                    birthdate: member.birthdate,
                    birthplace: member.birthplace,
                    first_name: member.first_name,
                    last_name: member.last_name,
                    gender: member.gender,
                    occupation: member.occupation || 'N/A',
                    relationship_to_head:
                      member.relationship_to_head || 'Family Member',
                  }))
              : [],
            household_members: resident.household_members
              ? resident.household_members.filter(
                  (member) => member.id !== resident.id
                )
              : [],
            created_at: resident.created_at,
            updated_at: resident.updated_at,
          };
          return transformed;
        });

        setResidents(transformedData);

        // Update pagination data from response
        if (response.data.pagination) {
          setPagination({
            currentPage: response.data.pagination.current_page,
            lastPage: response.data.pagination.last_page,
            perPage: response.data.pagination.per_page,
            total: response.data.pagination.total,
          });
        }
      } catch (error) {
        console.error('Error fetching residents:', error);
        // Set mock data as fallback
        const mockResidents = [
          {
            id: 1,
            first_name: 'Juan',
            last_name: 'Dela Cruz',
            date_of_birth: '1990-05-15',
            gender: 'male',
            civil_status: 'married',
            phone_number: '+63 912 345 6789',
            email: 'juan.delacruz@email.com',
            address: '123 Main Street, Barangay Center',
            status: 'active',
            barangay_id_number: 'BRG-2024-001',
            occupation: 'Teacher',
            emergency_contact: 'Maria Dela Cruz - +63 917 888 9999',
            photo_url: null,
            qr_code: 'BRG2024001JDC',
            household_id: 'HH-001',
            relationship_to_head: 'Head',
            family_members: [
              { name: 'Maria Dela Cruz', relationship: 'Spouse', age: 28 },
              { name: 'Jose Dela Cruz', relationship: 'Son', age: 5 },
              { name: 'Ana Dela Cruz', relationship: 'Daughter', age: 3 },
            ],
          },
        ];
        setResidents(mockResidents);
        setPagination({
          currentPage: 1,
          lastPage: 1,
          perPage: 10,
          total: mockResidents.length,
        });
      } finally {
        setLoading(false);
      }
    };

    initialLoad();
  }, []); // Empty dependency array for initial load only

  useEffect(() => {
    fetchRegions();
  }, []);

  useEffect(() => {
    if (!selectedRegion) return;
    fetchProvinces(selectedRegion);

    if (selectedRegion !== selectedResident?.region_code) {
      setValue('province', '');
      setValue('municipality', '');
      setValue('barangay', '');
    }
  }, [selectedRegion]);

  useEffect(() => {
    if (!selectedProvince) return;
    fetchMunicipalities(selectedProvince);

    if (selectedProvince !== selectedResident?.province_code) {
      setValue('municipality', '');
      setValue('barangay', '');
    }
  }, [selectedProvince]);

  useEffect(() => {
    if (!selectedMunicipality) return;
    fetchBarangays(selectedMunicipality);

    if (selectedMunicipality !== selectedResident?.municipality_code) {
      setValue('barangay', '');
    }
  }, [selectedMunicipality]);
  useEffect(() => {
    if (selectedResident?.province_code && provinces.length > 0) {
      setValue('province', selectedResident.province_code);
    }
  }, [provinces]);

  useEffect(() => {
    if (selectedResident?.municipality_code && municipalities.length > 0) {
      setValue('municipality', selectedResident.municipality_code);
    }
  }, [municipalities]);

  useEffect(() => {
    if (selectedResident?.barangay_code && barangays.length > 0) {
      setValue('barangay', selectedResident.barangay_code);
    }
  }, [barangays]);
  // Family modal state
  const [showCreateFamilyModal, setShowCreateFamilyModal] = useState(false);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [selectedHouseholdId, setSelectedHouseholdId] = useState(null);
  const [selectedFamilyId, setSelectedFamilyId] = useState(null);
  const [householdResidents, setHouseholdResidents] = useState([]);

  // Handler to open create family modal for a household
  const handleOpenCreateFamily = (householdId, residents) => {
    setSelectedHouseholdId(householdId);
    setHouseholdResidents(residents);
    setShowCreateFamilyModal(true);
  };

  // Assign household modal state
  const [showAssignHouseholdModal, setShowAssignHouseholdModal] =
    useState(false);
  const [residentToAssign, setResidentToAssign] = useState(null);

  // Handler to open assign household modal
  const handleOpenAssignHousehold = (resident) => {
    setResidentToAssign(resident);
    setShowAssignHouseholdModal(true);
  };

  const statsResident = stats(statistics);
  const [showAuditTrail, setShowAuditTrail] = useState(false);

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12  mx-auto'></div>
          <p className='mt-4 text-gray-600'>Loading residents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 animate-in fade-in duration-1000'>
      <div className='max-w-9xl mx-auto p-2'>
        {/* Header */}
        <div className='flex items-center justify-between mb-8 animate-in slide-in-from-top duration-700'>
          <div className='flex items-center space-x-4'>
            <div className='flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg animate-pulse'>
              <UserIcon className='w-6 h-6 text-blue-600' />
            </div>
            <div>
              <h1 className='text-2xl font-bold text-gray-900'>
                Residents Management
              </h1>
              <p className='text-gray-600'>
                Manage residents with digital ID and QR code generation
              </p>
            </div>
          </div>
          <div className='flex items-center space-x-3'>
            <button
              onClick={() => setShowAuditTrail(true)}
              className='inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-100 bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 hover:scale-105 active:scale-95'
            >
              <DocumentTextIcon className='w-4 h-4 mr-2' />
              Audit Trail
            </button>

            <button
              onClick={() => {
                setShowForm(true);
                setSelectedResident(null);
                setIsSubmitting(false);
                reset({
                  barangay_id_number: `BRGY-${new Date().getFullYear()}-${String(
                    Math.floor(Math.random() * 9999) + 1
                  ).padStart(4, '0')}`,
                });
              }}
              className='inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 hover:scale-105 active:scale-95'
            >
              <PlusIcon className='w-4 h-4 mr-2' />
              New Resident
            </button>
          </div>
        </div>

        {/* Audit Trail Modal */}
        {showAuditTrail && <AuditTrailModal setShowModal={setShowAuditTrail} />}

        <StatisticsCard
          stats={statsResident}
          columns='xl:grid-cols-5'
          text='text-xs'
        />
        {/* Demographics Dashboard */}
        <DemographicsDashboard
          residents={residents}
          statistics={statistics}
          showDetails={showDemographicsDetail}
          toggleDetails={() =>
            setShowDemographicsDetail(!showDemographicsDetail)
          }
        />

        <ResidentsTable
          residents={residents}
          isLoading={isTableLoading}
          onView={handleViewResident}
          onEdit={handleEditResident}
          onDelete={confirmDelete}
          onPrint={handlePrintResident}
          getPhotoUrl={getPhotoUrl}
          pagination={pagination}
          onPageChange={handlePageChange}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          filters={filters}
          setFilters={handleFiltersChange}
          search={filters.search}
          onSearchChange={handleSearchChange}
          onCreateFamily={(resident) => {
            // Use the resident's household for context
            const householdId = resident.household_id || resident.id;
            const householdRes = residents.filter(
              (r) => r.household_id === householdId || r.id === householdId
            );
            handleOpenCreateFamily(householdId, householdRes);
          }}
          onReassignHousehold={handleOpenAssignHousehold}
        />

        {/* Delete Confirmation Modal */}
        {showDeleteModal && residentToDelete && (
          <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
            <div className='relative top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-6 border w-full max-w-md shadow-2xl rounded-lg bg-white'>
              <div className='flex items-center justify-center mb-4'>
                <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100'>
                  <TrashIcon className='h-6 w-6 text-red-600' />
                </div>
              </div>

              <div className='text-center'>
                <h3 className='text-lg font-medium text-gray-900 mb-2'>
                  Delete Resident
                </h3>
                <p className='text-sm text-gray-500 mb-4'>
                  Are you sure you want to delete "{residentToDelete.first_name}{' '}
                  {residentToDelete.last_name}"? This action cannot be undone.
                </p>

                <div className='bg-gray-50 border border-gray-200 rounded-md p-3 mb-4'>
                  <div className='text-sm'>
                    <div className='font-medium text-gray-900'>
                      {residentToDelete.first_name} {residentToDelete.last_name}
                    </div>
                    <div className='text-gray-500'>
                      {residentToDelete.email || residentToDelete.phone_number}
                    </div>
                    <div className='text-xs text-gray-400 mt-1'>
                      {residentToDelete.gender} • {residentToDelete.status}
                    </div>
                  </div>
                </div>
              </div>

              <div className='flex justify-end gap-3'>
                <button
                  type='button'
                  onClick={cancelDelete}
                  className='px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={() => handleDeleteResident(residentToDelete.id)}
                  className='px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center gap-2'
                >
                  <TrashIcon className='h-4 w-4' />
                  Delete Resident
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Resident Modal */}
        <ViewResidentModal
          resident={selectedResident}
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedResident(null);
          }}
          onEdit={() => {
            setShowViewModal(false);
            if (selectedResident) {
              handleEditResident(selectedResident);
            }
          }}
        />

        {/* Print Modal */}
        <PrintModal
          resident={selectedResident}
          isOpen={showPrintModal}
          onClose={() => {
            setShowPrintModal(false);
            setSelectedResident(null);
          }}
        />

        {/* Resident Form Modal */}
        {showForm && (
          <ResidentFormModal
            isOpen={showForm}
            onClose={resetResidentForm}
            selectedResident={selectedResident}
            handleSubmit={handleSubmit}
            handleSubmitResident={handleSubmitResident}
            register={register}
            resetResidentForm={resetResidentForm}
            errors={errors}
            regions={regions}
            provinces={provinces}
            municipalities={municipalities}
            barangays={barangays}
            selectedRegion={selectedRegion}
            selectedProvince={selectedProvince}
            selectedMunicipality={selectedMunicipality}
            capturedPhoto={capturedPhoto}
            setCapturedPhoto={setCapturedPhoto}
            openPhotoCapture={openPhotoCapture}
            getResidentInitials={getResidentInitials}
            familyMembers={familyMembers}
            handleEditFamilyMember={handleEditFamilyMember}
            handleViewFamilyMember={handleViewFamilyMember}
            handleConfirmDeleteFamilyMember={handleConfirmDeleteFamilyMember}
            handleAddFamilyMember={handleAddFamilyMember}
            isDeletingFamilyMember={isDeletingFamilyMember}
            getPhotoUrl={getPhotoUrl}
            isSubmitting={isSubmitting}
          />
        )}

        {/* Family Member Form Modal */}
        <FamilyMemberFormModal
          show={showFamilyMemberForm}
          onClose={handleCancelFamilyMemberForm}
          familyMember={newFamilyMember}
          onChange={setNewFamilyMember}
          onSave={handleSaveFamilyMember}
          onCancel={handleCancelFamilyMemberForm}
          isEditing={!!editingFamilyMember}
          currentHouseholdHeadId={selectedResident?.id}
        />

        {/* Family Member View Modal */}
        <FamilyMemberViewModal
          show={showFamilyMemberViewModal}
          onClose={() => setShowFamilyMemberViewModal(false)}
          familyMember={selectedFamilyMember}
          onEdit={handleEditFamilyMember}
          onDelete={handleConfirmDeleteFamilyMember}
          /* onDetach={handleDetachFamilyMember} */
        />

        {/* Delete Family Member Modal */}
        <DeleteFamilyMemberModal
          show={showDeleteFamilyMemberModal}
          onClose={handleCancelDeleteFamilyMember}
          familyMember={familyMemberToDelete}
          onConfirm={handleDeleteFamilyMember}
          isDeleting={isDeletingFamilyMember}
        />

        {/* Photo Capture Modal */}
        <PhotoCaptureModal
          show={showPhotoCaptureModal}
          onClose={() => setShowPhotoCaptureModal(false)}
          onCapture={handlePhotoCaptured}
          capturedPhoto={capturedPhoto}
          setCapturedPhoto={setCapturedPhoto}
        />

        {/* Create Family Modal */}
        <CreateFamilyModal
          show={showCreateFamilyModal}
          onClose={() => setShowCreateFamilyModal(false)}
          householdId={selectedHouseholdId}
          residents={householdResidents}
          onFamilyCreated={() => {
            /* Optionally refresh families/households */
          }}
        />

        {/* Family Modal */}
        <FamilyModal
          show={showFamilyModal}
          onClose={() => setShowFamilyModal(false)}
          familyId={selectedFamilyId}
        />

        {/* Assign Household Modal */}
        <AssignHouseholdModal
          show={showAssignHouseholdModal}
          onClose={() => setShowAssignHouseholdModal(false)}
          resident={residentToAssign}
          onAssigned={() => {
            setShowAssignHouseholdModal(false);
            refreshResidents();
          }}
        />
      </div>
    </div>
  );
};

export default ResidentsPage;
